import { useState, useEffect } from "react";
import { Box, Typography, FormControl, FormControlLabel, Radio, RadioGroup, TextField, InputLabel, Select, MenuItem, Grid, IconButton, Tooltip, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import RpPeriodSlider from "@plannel/components/RpPeriodSlider";
import PlanStrategyHelp from "@plannel/components/PlanStrategyHelp";
import "@plannel/scss/components/DataLoadValidation.scss"
import { withTranslation } from "react-i18next";
import CodeUtil from "@plannel/pages/utils/code-util";
import { isEqual } from "lodash";
import rpUtil from "@plannel/pages/replenishment-plan/utils/rp-util";
import { addDays, addMonths, startOfMonth } from "date-fns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  getNextMonday, getNextFirstOfMonth,
  ALL_MP_BUCKET_OPTIONS, getAllowedBucketsByPrevious, getAllowedBucketsByNext,
  convertBucketCount, enforceTimeBucketProgression,
  calcSegmentStartDatesWithBoundary, calcWeekCountToNextMonth, calcWeekCountBeforeMonth,
  calcPartialWeekCountToDate, calcPartialWeekCountBeforeMonth, initializeTimeBuckets,
} from "@plannel/pages/master-plan/utils/mp-bucket-util";
import { FS } from "@plannel/utils/font-util";

const ROLE_APP_RP = "ROLE_APP_RP";

const { priorityItems, rpDemandDistributionPolicy, buckets, module, mpPlanConfig: { SAFETY_STOCK_POLICY, KIT_PLAN_MODE, ALLOW_SEMI_ORDER, ALT_RES_POLICY }, itemType } = CodeUtil;
const { configDefaultValue } = rpUtil;
const { DATE_PRIORITY: { DUE_DATE }, DEMAND_PRIORITY: { STD_UNIT_PRICE }, PRODUCTION_PRIORITY: { FAST, PRIORITY, MASTER_DATA } } = priorityItems;
const now = new ZDate();
const { MONTH, WEEK, PARTIAL_WEEK, DAY } = buckets;

const mpConfigDefaultValue = {
  bucket: DAY,
  range: 90,
  start: now,
  end: addDays(now, 90),
  resourceCapacity: "finite",
  materialConst: "masterData",
  planStrategy: "forward",
  backorder: "allow",
  frozenRange: 0,
  demandPriority: DUE_DATE,
  datePriority: STD_UNIT_PRICE,
  safetyStockPolicy: "NONE",
  kitPlanMode: false,
  allowSemiOrder: false,
  altResPolicy: FAST,
  enableRollingPlan: false
}

const RP_TIME_DIMENSIOMS = {
  DAY: { cd: DAY, name: "daily" },
  WEEK: { cd: WEEK, name: "week" },
  MONTH: { cd: MONTH, name: "monthly" }
}
const { EQUAL_BY_BUCKET, LAST_OF_BUCKET } = rpDemandDistributionPolicy;
const SALES = "SALES";
const DEMAND = "DEMAND";
const SALES_REF_START = "SALES_REF_START";
const DEMAND_REF_START = "DEMAND_REF_START";
const SALES_REF_END = "SALES_REF_END";
const DEMAND_REF_END = "DEMAND_REF_END";

const MAX_BUCKET_COUNT = 999;

const createBucketSegment = (startOffset, endOffset, bucket) => ({
  id: `${startOffset}-${endOffset}-${bucket}-${Date.now()}`,
  startOffset,
  endOffset,
  bucket,
});

const buildDefaultBucketSegments = (range, bucket = DAY) => {
  const totalRange = Math.max(1, Number(range) || 1);
  return [createBucketSegment(0, totalRange - 1, bucket)];
};

const normalizeBucketSegments = (segments, range, fallbackBucket = DAY) => {
  const totalRange = Math.max(1, Number(range) || 1);
  const maxEnd = totalRange - 1;

  if (!Array.isArray(segments) || segments.length === 0) {
    return buildDefaultBucketSegments(totalRange, fallbackBucket);
  }

  const sortedSegments = segments
    .map((segment, index) => ({
      id: segment?.id ?? `${index}-${Date.now()}`,
      startOffset: Number.isFinite(Number(segment?.startOffset)) ? Number(segment?.startOffset) : 0,
      endOffset: Number.isFinite(Number(segment?.endOffset)) ? Number(segment?.endOffset) : maxEnd,
      bucket: segment?.bucket ?? DAY,
    }))
    .sort((left, right) => left.startOffset - right.startOffset);

  const normalized = [];
  let nextStart = 0;

  sortedSegments.forEach((segment, index) => {
    if (nextStart > maxEnd) {
      return;
    }

    const startOffset = index === 0 ? 0 : Math.max(nextStart, Math.min(segment.startOffset, maxEnd));
    const endOffset = index === sortedSegments.length - 1
      ? maxEnd
      : Math.min(Math.max(segment.endOffset, startOffset), maxEnd);

    if (startOffset > endOffset) {
      return;
    }

    normalized.push({
      id: segment.id,
      startOffset,
      endOffset,
      bucket: segment.bucket,
    });

    nextStart = endOffset + 1;
  });

  if (normalized.length === 0) {
    return buildDefaultBucketSegments(totalRange, fallbackBucket);
  }

  normalized[0] = {
    ...normalized[0],
    startOffset: 0,
  };

  for (let index = 1; index < normalized.length; index += 1) {
    normalized[index] = {
      ...normalized[index],
      startOffset: normalized[index - 1].endOffset + 1,
    };
  }

  normalized[normalized.length - 1] = {
    ...normalized[normalized.length - 1],
    endOffset: maxEnd,
  };

  return normalized;
};


const enforceBucketProgression = (segments, range, fallbackBucket = DAY) => {
  const normalized = normalizeBucketSegments(segments, range, fallbackBucket);
  if (normalized.length === 0) {
    return normalized;
  }

  const firstBucket = ALL_MP_BUCKET_OPTIONS.includes(normalized[0].bucket) ? normalized[0].bucket : fallbackBucket;
  const progressedSegments = [{ ...normalized[0], bucket: firstBucket }];

  for (let index = 1; index < normalized.length; index += 1) {
    const previousBucket = progressedSegments[index - 1].bucket;
    const allowedBuckets = getAllowedBucketsByPrevious(previousBucket);

    progressedSegments.push({
      ...normalized[index],
      bucket: allowedBuckets.includes(normalized[index].bucket) ? normalized[index].bucket : allowedBuckets[0],
    });
  }

  return progressedSegments;
};

const getBucketLabelKey = bucket => {
  if (bucket === DAY) {
    return "daily";
  }
  if (bucket === WEEK) {
    return "week";
  }
  if (bucket === PARTIAL_WEEK) {
    return "partialWeek";
  }
  if (bucket === MONTH) {
    return "monthly";
  }
  return bucket;
};


const MpBucketStrategySettings = ({
  t,
  timeBuckets,
  setTimeBuckets,
  compact = false,
  planStartDate = null,
  onPlanStartDateChange = null,
  onlyLastSegmentCount = false,
}) => {
  const normalized = enforceTimeBucketProgression(timeBuckets);
  const segmentStartDates = calcSegmentStartDatesWithBoundary(planStartDate, normalized);

  const getAllowedUnitsForIndex = (index) => {
    const prevUnit = index > 0 ? normalized[index - 1]?.UNIT : null;
    const nextUnit = index < normalized.length - 1 ? normalized[index + 1]?.UNIT : null;

    let options = prevUnit ? getAllowedBucketsByPrevious(prevUnit) : ALL_MP_BUCKET_OPTIONS;

    if (nextUnit) {
      const allowedByNext = getAllowedBucketsByNext(nextUnit);
      options = options.filter(unit => allowedByNext.includes(unit));
    }

    return options;
  };

  const canAddBucket = normalized.length === 0 || normalized[normalized.length - 1]?.UNIT !== MONTH;

  const handleCountChange = (index, value) => {
    const count = parseInt(value, 10);
    if (!Number.isFinite(count) || count < 1 || count > MAX_BUCKET_COUNT) return;
    setTimeBuckets(prev =>
      enforceTimeBucketProgression(prev).map((tb, i) => i === index ? { ...tb, COUNT: count } : tb)
    );
  };

  const handleUnitChange = (index, newUnit) => {
    setTimeBuckets(prev => {
      let cur = enforceTimeBucketProgression(prev);
      const isLastSegment = index === cur.length - 1;
      const originalUnit = cur[index]?.UNIT;
      const originalCount = cur[index]?.COUNT ?? 1;

      if (planStartDate && index > 0) {
        const prevUnit = cur[index - 1].UNIT;
        const allStarts = calcSegmentStartDatesWithBoundary(planStartDate, cur);
        const segStart = allStarts[index];
        const prevSegStart = allStarts[index - 1];

        if ((newUnit === WEEK || newUnit === PARTIAL_WEEK) && prevUnit === DAY
            && !(originalUnit === WEEK || originalUnit === PARTIAL_WEEK)) {
          const nextMonday = getNextMonday(segStart);
          if (nextMonday && prevSegStart) {
            const newPrevCount = Math.max(1, Math.round((nextMonday - prevSegStart) / 86400000));
            if (isLastSegment) {
              // For last segment: only snap prev; count is duration-converted in final map
              cur = cur.map((tb, i) => i === index - 1 ? { ...tb, COUNT: newPrevCount } : tb);
            } else {
              const newWeekCount = newUnit === WEEK
                ? calcWeekCountBeforeMonth(nextMonday, Math.ceil(Math.round((allStarts[index + 1] - nextMonday) / 86400000) / 7))
                : calcPartialWeekCountBeforeMonth(nextMonday, calcPartialWeekCountToDate(nextMonday, allStarts[index + 1]));
              cur = cur.map((tb, i) => {
                if (i === index - 1) return { ...tb, COUNT: newPrevCount };
                if (i === index) return { ...tb, COUNT: newWeekCount };
                return tb;
              });
            }
          }
        } else if (newUnit === MONTH && prevUnit === DAY) {
          const nextFirst = getNextFirstOfMonth(segStart);
          if (nextFirst && prevSegStart) {
            const newCount = Math.max(1, Math.round((nextFirst - prevSegStart) / 86400000));
            cur = cur.map((tb, i) => i === index - 1 ? { ...tb, COUNT: newCount } : tb);
          }
        } else if (
          (originalUnit === WEEK || originalUnit === PARTIAL_WEEK) &&
          (newUnit === WEEK || newUnit === PARTIAL_WEEK) &&
          originalUnit !== newUnit &&
          !isLastSegment
        ) {
          // WEEK ↔ PARTIAL_WEEK 전환 시 다음 세그먼트 시작일 기준으로 버킷 수 재계산
          const nextStart = allStarts[index + 1];
          if (nextStart && segStart) {
            let newCount;
            if (newUnit === PARTIAL_WEEK) {
              // 다음 세그먼트(월별)의 월 경계를 목표로 PARTIAL_WEEK 버킷 수 계산
              const targetDate = new ZDate(startOfMonth(nextStart));
              newCount = calcPartialWeekCountToDate(segStart, targetDate);
            } else {
              // PARTIAL_WEEK → WEEK: (nextStart - segStart) 를 7일 단위로 올림
              const diffDays = Math.round((nextStart - segStart) / 86400000);
              newCount = calcWeekCountBeforeMonth(segStart, Math.ceil(diffDays / 7));
            }
            cur = cur.map((tb, i) => i === index ? { ...tb, COUNT: newCount } : tb);
          }
        }
        // WEEK/PARTIAL_WEEK → MONTH: no adjustment needed
      } else if (planStartDate && index === 0) {
        if (onPlanStartDateChange) {
          const segStart = new ZDate(planStartDate);
          if (newUnit === WEEK || newUnit === PARTIAL_WEEK) {
            const nextMonday = getNextMonday(segStart);
            if (nextMonday - segStart > 0) onPlanStartDateChange(nextMonday);
          } else if (newUnit === MONTH) {
            const nextFirst = getNextFirstOfMonth(segStart);
            if (nextFirst - segStart > 0) onPlanStartDateChange(nextFirst);
          }
        }
        // When there is a following segment, recalculate count targeting its current start date
        if (!isLastSegment && originalUnit !== newUnit) {
          const allStarts = calcSegmentStartDatesWithBoundary(planStartDate, cur);
          const nextStart = allStarts[1];
          if (nextStart) {
            // Use the snapped start (Monday for WEEK, 1st for MONTH) when onPlanStartDateChange
            // is available — that's when the planStartDate will actually be snapped.
            // Without it (popup), planStartDate is fixed so use it as-is.
            const effectiveStart = onPlanStartDateChange
              ? ((newUnit === WEEK || newUnit === PARTIAL_WEEK)
                  ? (getNextMonday(new ZDate(planStartDate)) ?? new ZDate(planStartDate))
                  : newUnit === MONTH
                    ? (getNextFirstOfMonth(new ZDate(planStartDate)) ?? new ZDate(planStartDate))
                    : new ZDate(planStartDate))
              : new ZDate(planStartDate);
            const diffMs = nextStart - effectiveStart;
            let newFirstCount;
            if (newUnit === WEEK) {
              newFirstCount = calcWeekCountBeforeMonth(effectiveStart, Math.ceil(Math.round(diffMs / 86400000) / 7));
            } else if (newUnit === PARTIAL_WEEK) {
              newFirstCount = calcPartialWeekCountToDate(effectiveStart, nextStart);
            } else if (newUnit === MONTH) {
              const months = (nextStart.getFullYear() - effectiveStart.getFullYear()) * 12
                + (nextStart.getMonth() - effectiveStart.getMonth());
              newFirstCount = Math.max(1, months);
            } else {
              newFirstCount = Math.max(1, Math.round(diffMs / 86400000));
            }
            cur = cur.map((tb, i) => i === 0 ? { ...tb, COUNT: newFirstCount } : tb);
          }
        }
      }

      return enforceTimeBucketProgression(
        cur.map((tb, i) => {
          if (i !== index) return tb;
          if (isLastSegment && originalUnit !== newUnit) {
            return { ...tb, UNIT: newUnit, COUNT: convertBucketCount(originalUnit, newUnit, originalCount) };
          }
          return { ...tb, UNIT: newUnit };
        })
      );
    });
  };

  const handleStartDateChange = (index, newDate) => {
    if (!newDate || !planStartDate) return;

    const unit = normalized[index]?.UNIT;
    const snapped = (() => {
      if (unit === WEEK || unit === PARTIAL_WEEK) return getNextMonday(newDate);
      if (unit === MONTH) return getNextFirstOfMonth(newDate);
      return new ZDate(newDate);
    })();

    if (index === 0) {
      if (onPlanStartDateChange) {
        setTimeBuckets(prev => {
          const cur = enforceTimeBucketProgression(prev);
          const n = cur.length;
          if (n === 0) return cur;

          // Cascade from new plan start: keep each segment's count, increase only when
          // the existing count is insufficient to reach the next segment's valid boundary.
          // Last segment: count unchanged.
          const newCounts = cur.map(tb => Math.max(1, tb.COUNT || 1));
          let segStart = snapped;

          for (let i = 0; i < n - 1; i++) {
            const tb = cur[i];
            let count = newCounts[i];

            if (tb.UNIT === DAY) {
              // Fix next segment's existing start; recalculate DAY count as the exact difference.
              // DAY acts as a gap-filler: when plan start shifts, only DAY count changes.
              const nextExistingStart = segmentStartDates[i + 1];
              if (nextExistingStart) {
                count = Math.max(1, Math.round((nextExistingStart - segStart) / 86400000));
              }
              newCounts[i] = count;
              segStart = addDays(segStart, count);
            } else if (tb.UNIT === WEEK) {
              count = calcWeekCountBeforeMonth(segStart, count);
              newCounts[i] = count;
              segStart = new ZDate(startOfMonth(addDays(segStart, count * 7)));
            } else if (tb.UNIT === PARTIAL_WEEK) {
              count = calcPartialWeekCountBeforeMonth(segStart, count);
              newCounts[i] = count;
              segStart = calcSegmentStartDatesWithBoundary(segStart, [{ UNIT: PARTIAL_WEEK, COUNT: count }, { UNIT: MONTH, COUNT: 1 }])[1];
            } else if (tb.UNIT === MONTH) {
              newCounts[i] = count;
              segStart = addMonths(segStart, count);
            }
          }

          return cur.map((tb, i) => ({ ...tb, COUNT: newCounts[i] }));
        });
        onPlanStartDateChange(snapped);
      }
      return;
    }

    setTimeBuckets(prev => {
      const cur = enforceTimeBucketProgression(prev);
      const allStarts = calcSegmentStartDatesWithBoundary(planStartDate, cur);
      const prevSegStart = allStarts[index - 1];
      if (!prevSegStart) return cur;

      const prevUnit = cur[index - 1].UNIT;
      const diffMs = snapped - prevSegStart;
      let newPrevCount;

      if (prevUnit === DAY) {
        newPrevCount = Math.max(1, Math.round(diffMs / 86400000));
      } else if (prevUnit === WEEK) {
        newPrevCount = Math.max(1, Math.ceil(diffMs / (7 * 86400000)));
      } else if (prevUnit === PARTIAL_WEEK) {
        newPrevCount = calcPartialWeekCountToDate(prevSegStart, snapped);
      } else if (prevUnit === MONTH) {
        const months = (snapped.getFullYear() - prevSegStart.getFullYear()) * 12
          + (snapped.getMonth() - prevSegStart.getMonth());
        newPrevCount = Math.max(1, months);
      } else {
        return cur;
      }

      return enforceTimeBucketProgression(cur.map((tb, i) => {
        if (i === index - 1) return { ...tb, COUNT: newPrevCount };
        if (i === index) {
          const nextStart = allStarts[index + 1] ?? null;
          if (unit === WEEK && nextStart) {
            const weekCount = snapped > nextStart ? 1 : Math.ceil(Math.round((nextStart - snapped) / 86400000) / 7);
            return { ...tb, COUNT: calcWeekCountBeforeMonth(snapped, weekCount) };
          }
          if (unit === PARTIAL_WEEK && nextStart) {
            const partialCount = snapped > nextStart ? 1 : calcPartialWeekCountToDate(snapped, nextStart);
            return { ...tb, COUNT: calcPartialWeekCountBeforeMonth(snapped, partialCount) };
          }
          if (unit === WEEK || unit === PARTIAL_WEEK) {
            // If snapped is past nextStart, target the next month boundary after snapped
            if (!nextStart || snapped > nextStart) return { ...tb, COUNT: calcWeekCountToNextMonth(snapped) };
            const newCount = Math.max(1, Math.ceil((nextStart - snapped) / (7 * 86400000)));
            return { ...tb, COUNT: newCount };
          }
          if (unit === DAY && nextStart) {
            const newCount = Math.max(1, Math.round((nextStart - snapped) / 86400000));
            return { ...tb, COUNT: newCount };
          }
          if (unit === MONTH && nextStart) {
            const months = (nextStart.getFullYear() - snapped.getFullYear()) * 12
              + (nextStart.getMonth() - snapped.getMonth());
            return { ...tb, COUNT: Math.max(1, months) };
          }
        }
        return tb;
      }));
    });
  };

  const handleAddBucket = () => {
    setTimeBuckets(prev => {
      let cur = enforceTimeBucketProgression(prev);
      const lastUnit = cur[cur.length - 1]?.UNIT ?? DAY;
      const allowed = getAllowedBucketsByPrevious(lastUnit);
      const newUnit = allowed[0];
      let newCount = 1;

      if (planStartDate && cur.length > 0) {
        const allStarts = calcSegmentStartDatesWithBoundary(planStartDate, cur);
        const prevSegStart = allStarts[allStarts.length - 1];
        const lastTb = cur[cur.length - 1];
        const lastCount = Math.max(1, lastTb?.COUNT || 1);

        // Calculate raw start of new segment from last segment's end
        let newSegRawStart = null;
        if (lastTb?.UNIT === DAY) {
          newSegRawStart = addDays(prevSegStart, lastCount);
        } else if (lastTb?.UNIT === WEEK || lastTb?.UNIT === PARTIAL_WEEK) {
          newSegRawStart = new ZDate(startOfMonth(addDays(prevSegStart, lastCount * 7)));
        } else if (lastTb?.UNIT === MONTH) {
          newSegRawStart = addMonths(prevSegStart, lastCount);
        }

        if (newSegRawStart) {
          if (newUnit === WEEK || newUnit === PARTIAL_WEEK) {
            // Snap to next Monday; only increase DAY count if not already on a Monday
            const nextMonday = getNextMonday(newSegRawStart);
            if (nextMonday && prevSegStart) {
              const neededDayCount = Math.max(1, Math.round((nextMonday - prevSegStart) / 86400000));
              if (lastCount < neededDayCount) {
                cur = cur.map((tb, i) => i === cur.length - 1 ? { ...tb, COUNT: neededDayCount } : tb);
              }
            }
            newCount = 1;
          } else if (newUnit === MONTH && lastUnit === DAY) {
            // DAY → MONTH: snap to next 1st of month; only increase DAY count if not already on 1st
            const nextFirst = getNextFirstOfMonth(newSegRawStart);
            if (nextFirst && prevSegStart) {
              const neededDayCount = Math.max(1, Math.round((nextFirst - prevSegStart) / 86400000));
              if (lastCount < neededDayCount) {
                cur = cur.map((tb, i) => i === cur.length - 1 ? { ...tb, COUNT: neededDayCount } : tb);
              }
            }
          } else if (newUnit === MONTH && lastUnit === WEEK) {
            const weekCount = calcWeekCountBeforeMonth(prevSegStart, lastCount);
            cur = cur.map((tb, i) => i === cur.length - 1 ? { ...tb, COUNT: weekCount } : tb);
          } else if (newUnit === MONTH && lastUnit === PARTIAL_WEEK) {
            const partialCount = calcPartialWeekCountBeforeMonth(prevSegStart, lastCount);
            cur = cur.map((tb, i) => i === cur.length - 1 ? { ...tb, COUNT: partialCount } : tb);
          }
        }
      }

      return enforceTimeBucketProgression([...cur, { UNIT: newUnit, COUNT: newCount }]);
    });
  };

  const handleRemoveBucket = (index) => {
    const cur = enforceTimeBucketProgression(timeBuckets);
    if (cur.length <= 1) return;
    const isLastSegment = index === cur.length - 1;
    const removed = enforceTimeBucketProgression([...cur.slice(0, index), ...cur.slice(index + 1)]);
    // When deleting the last segment, keep the remaining counts as-is.
    // initializeTimeBuckets would recalculate WEEK counts via calcWeekCountToNextMonth,
    // which reduces an already-sufficient count.
    if (!planStartDate || isLastSegment) {
      setTimeBuckets(removed);
      return;
    }
    const { newPlanStart, newTbs } = initializeTimeBuckets(planStartDate, removed);
    setTimeBuckets(newTbs);
    if (onPlanStartDateChange && newPlanStart && new ZDate(planStartDate).getTime() !== newPlanStart.getTime()) {
      onPlanStartDateChange(newPlanStart);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: "100%" }}>
        {!compact && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: FS.H2, mb: 0.5 }}>
              {t("bucketStrategy")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: FS.TITLE, lineHeight: 1.7 }}>
              {t("bucketStrategyDesc")}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: compact ? 0 : 1 }}>
          {/* 라벨 태그 + 구간별 칩 파이프라인 (28 Daily → 4 Week → 3 Monthly) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
            <Box component="span" sx={{
              bgcolor: "primary.main", color: "primary.contrastText",
              fontSize: FS.BODY, fontWeight: 600, letterSpacing: 0.3,
              px: 1.1, py: 0.4, borderRadius: 1.25,
            }}>
              {t("bucketSegmentSummary")}
            </Box>
            {normalized.map((tb, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {i > 0 && <Box component="span" sx={{ color: "primary.main", fontSize: FS.TITLE, lineHeight: 1 }}>→</Box>}
                <Chip
                  size="small"
                  label={
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>{tb.COUNT}</Box>
                      <Box component="span" sx={{ fontWeight: 400 }}>{t(getBucketLabelKey(tb.UNIT))}</Box>
                    </Box>
                  }
                  sx={{
                    height: 24, fontSize: FS.SUB, borderRadius: 1.5,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: "primary.dark",
                  }}
                />
              </Box>
            ))}
          </Box>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, px: 1.5, py: 0.5, width: "fit-content", maxWidth: "100%" }}>
            <Box>
              {normalized.map((tb, index) => {
                const isMonth = tb.UNIT === MONTH;
                const isWeekType = tb.UNIT === WEEK || tb.UNIT === PARTIAL_WEEK;
                const isFirstSeg = index === 0;
                const startDateVal = segmentStartDates[index] ?? null;

                // maxDate: one day before the next segment's start date
                const nextSegStart = index < normalized.length - 1 ? (segmentStartDates[index + 1] ?? null) : null;
                const maxDate = nextSegStart ? addDays(nextSegStart, -1) : undefined;

                // minDate: ensure the previous segment keeps at least 1 bucket.
                // Snap the minimum to the current segment's required boundary (Monday / 1st).
                const prevSegStart = index > 0 ? (segmentStartDates[index - 1] ?? null) : null;
                const prevUnit = index > 0 ? normalized[index - 1]?.UNIT : null;
                let minDate;
                if (index > 0 && prevSegStart && prevUnit) {
                  if (prevUnit === DAY) {
                    const minRequired = addDays(prevSegStart, 1);
                    if (isMonth) minDate = getNextFirstOfMonth(minRequired) ?? minRequired;
                    else if (isWeekType) minDate = getNextMonday(minRequired) ?? minRequired;
                    else minDate = minRequired;
                  } else if (prevUnit === WEEK || prevUnit === PARTIAL_WEEK) {
                    if (isMonth) {
                      // calcSegmentStartDatesWithBoundary uses startOfMonth (not getNextFirstOfMonth),
                      // so derive minDate the same way to avoid false error state.
                      const minWeekCount = calcWeekCountToNextMonth(prevSegStart);
                      minDate = new ZDate(startOfMonth(addDays(prevSegStart, minWeekCount * 7)));
                    } else if (isWeekType) {
                      minDate = getNextMonday(addDays(prevSegStart, 7)) ?? addDays(prevSegStart, 7);
                    } else {
                      minDate = addDays(prevSegStart, 7);
                    }
                  } else if (prevUnit === MONTH) {
                    const minRequired = addMonths(prevSegStart, 1);
                    if (isMonth) minDate = minRequired; // already the 1st
                    else if (isWeekType) minDate = getNextMonday(minRequired) ?? minRequired;
                    else minDate = minRequired;
                  }
                }

                // DatePicker props differ by bucket type
                const datePickerProps = isMonth
                  ? { views: ["year", "month"], format: "yyyy-MM", openTo: "month" }
                  : { views: ["year", "month", "day"], format: "yyyy-MM-dd", openTo: "day",
                      shouldDisableDate: isWeekType ? (d) => new Date(d).getDay() !== 1 : undefined };

                // Start date read-only only for first segment without callback (version popup)
                const startReadOnly = isFirstSeg && !onPlanStartDateChange;

                return (
                  <Box key={`${index}-${tb.UNIT}`}>
                    <Box sx={{ display: "flex", flexWrap: "nowrap", gap: compact ? 1 : 1.5, alignItems: "center", justifyContent: "flex-start", py: compact ? 1 : 1.5, borderBottom: index === normalized.length - 1 ? "none" : "1px solid", borderColor: "divider" }}>
                      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: compact ? 1 : 1.5, alignItems: "center", flex: "0 1 auto", minWidth: 0 }}>
                        <Typography sx={{ minWidth: compact ? 20 : 24, fontWeight: 600, textAlign: "center", flexShrink: 0 }}>
                          {index + 1}
                        </Typography>
                        <FormControl size="small" sx={{ flex: "1 1 120px", minWidth: compact ? 100 : 110, maxWidth: 155 }}>
                          <InputLabel>{t("segmentBucket")}</InputLabel>
                          <Select
                            label={t("segmentBucket")}
                            variant="outlined"
                            value={tb.UNIT}
                            onChange={e => handleUnitChange(index, e.target.value)}
                          >
                            {getAllowedUnitsForIndex(index).map(unit => (
                              <MenuItem key={unit} value={unit}>{t(getBucketLabelKey(unit))}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label={t("bucketCount")}
                          size="small"
                          value={tb.COUNT}
                          type="number"
                          sx={{ flex: "1 1 90px", minWidth: compact ? 55 : 65, maxWidth: 90 }}
                          inputProps={{ min: 1, max: MAX_BUCKET_COUNT, readOnly: onlyLastSegmentCount && index !== normalized.length - 1 }}
                          onChange={e => handleCountChange(index, e.target.value)}
                        />
                        {planStartDate && (
                          <DatePicker
                            {...datePickerProps}
                            label={t("startDate")}
                            value={startDateVal}
                            minDate={minDate}
                            maxDate={maxDate}
                            onAccept={(newDate) => handleStartDateChange(index, newDate)}
                            readOnly={startReadOnly}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: { flex: "2 1 160px", minWidth: compact ? 135 : 160, maxWidth: 195 },
                                inputProps: { readOnly: startReadOnly }
                              }
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                        <Tooltip title={t("removeSegment")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveBucket(index)}
                              disabled={normalized.length <= 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {canAddBucket && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
                <Tooltip title={t("addSegment")}>
                  <IconButton size="small" onClick={handleAddBucket}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

// RP Module Settings Component
const RpModuleSettings = ({
  t, kitDemandMergeFlg, noteFlg, multiSourcingFlg, useTargetSupplyDays, immediateShipment,
  setImmediateShipment, handleCheckbox, backorder, setBackorder
}) => {
  return (
    <Grid container spacing={2} sx={{ pl: 0 }}>
      {/* Select options - 2 columns per row */}
      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", maxWidth: "300px" }}>
          <InputLabel>{t("backorder")}</InputLabel>
          <Select
            variant="outlined"
            name="backorder"
            label={t("backorder")}
            value={backorder}
            size="small"
            onChange={event => {
              setBackorder(event.target.value);
            }}>
            <MenuItem value="notAllow" key="notAllow"> {t("notAllow")}</MenuItem>
            <MenuItem value="allow" key="allow"> {t("allow")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", maxWidth: "300px" }}>
          <InputLabel>{t("shipmentTime")}</InputLabel>
          <Select
            variant="outlined"
            name="shipmentTime"
            label={t("shipmentTime")}
            value={immediateShipment}
            size="small"
            onChange={event => {
              setImmediateShipment(event.target.value);
            }}>
            <MenuItem value="shipmentSame" key="shipmentSame"> {t("shipmentSame")}</MenuItem>
            <MenuItem value="shipmentNext" key="shipmentNext"> {t("shipmentNext")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {/* Empty Grid item for 3-column alignment - hidden on small screens */}
      <Grid item md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />

      {/* Switch options - single column */}
      <Grid item xs={12} sx={{ pl: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box component="label" key="KIT_DEMAND_MERGED_FLG" sx={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer", width: "fit-content" }}>
            <StyledSwitch size="small" name="kitSalesDemandMergeFlg" checked={kitDemandMergeFlg} onChange={(param) => handleCheckbox(param, "kitSalesDemandMergeFlg")} />
            <Typography variant="body2">{t("kitSalesDemandMergeFlg")}</Typography>
          </Box>
          <Box component="label" key="USE_TARGET_SUPPLY_DAYS" sx={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer", width: "fit-content" }}>
            <StyledSwitch size="small" name="useTargetSupplyDays" checked={useTargetSupplyDays} onChange={(param) => handleCheckbox(param, "useTargetSupplyDays")} />
            <Typography variant="body2">{t("useTargetSupplyDays")}</Typography>
          </Box>
          <Box component="label" key="NOTE_FLG" sx={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer", width: "fit-content" }}>
            <StyledSwitch size="small" name="noteFlg" checked={noteFlg} onChange={param => handleCheckbox(param, "noteFlg")} />
            <Typography variant="body2">{t("noteCopy")}</Typography>
          </Box>
          <Box component="label" key="MULTI_SOURCING_FLG" sx={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer", width: "fit-content" }}>
            <StyledSwitch size="small" name="multiSourcingFlg" checked={multiSourcingFlg} onChange={param => handleCheckbox(param, "multiSourcingFlg")} />
            <Typography variant="body2">{t("useMultiSourcing")}</Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

// MP Module Settings Component
const MpModuleSettings = ({
  t, userRoles, resourceCapacity, setResourceCapacity, materialConstraint, setMaterialConstraint,
  planStrategy, setPlanStrategy, backorder, setBackorder, safetyStockPolicy, setSafetyStockPolicy,
  kitPlanMode, setKitPlanMode, allowSemiOrder, setAllowSemiOrder, alterResPolicy, setAlterResPolicy
}) => {
  // 포워드 계열(forward / forward2) — 드롭다운에는 포워드로 보이고, 둘의 구분은 "계획 기준" 선택 칸이 나타낸다.
  const isForward = planStrategy === "forward" || planStrategy === "forward2";

  return (
    <Grid container spacing={2} sx={{ pl: 0 }}>
      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
          <InputLabel>{t("resourceCapa")}</InputLabel>
          <Select
            variant="outlined"
            name="resourceCapa"
            label={t("resourceCapa")}
            value={resourceCapacity}
            size="small"
            onChange={event => {
              setResourceCapacity(event.target.value);
            }}>
            <MenuItem value="infinite" key="infinite"> {t("infinite")}</MenuItem>
            <MenuItem value="finite" key="finite"> {t("finite")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
          <InputLabel>{t("materialConst")}</InputLabel>
          <Select
            variant="outlined"
            name="materialConst"
            label={t("materialConst")}
            value={materialConstraint}
            size="small"
            onChange={event => {
              setMaterialConstraint(event.target.value);
            }}>
            <MenuItem value="infinite" key="infinite"> {t("infinite")}</MenuItem>
            <MenuItem value="finite" key="finite"> {t("finite")}</MenuItem>
            <MenuItem value="masterData" key="masterData"> {t("masterData")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {/* Empty Grid item for 3-column alignment - hidden on small screens */}
      <Grid item md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />

      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
          <InputLabel>{t("planStrategy")}</InputLabel>
          <Select
            variant="outlined"
            name="planStrategy"
            label={t("planStrategy")}
            value={isForward ? "forward" : planStrategy}
            size="small"
            onChange={event => {
              setPlanStrategy(event.target.value);
            }}>
            <MenuItem value="forward" key="forward"> {t("forward")}</MenuItem>
            <MenuItem value="backward" key="backward"> {t("backward")}</MenuItem>
          </Select>
        </FormControl>
        {/* forward2 는 선택지 대신 "계획 기준"으로 고른다 — 포워드일 때만 노출 (가용 시점 기준=forward / 납기 기준=forward2). */}
        {isForward && (
          <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px", mt: 2 }}>
            <InputLabel>{t("planningBasis")}</InputLabel>
            <Select
              variant="outlined"
              name="planningBasis"
              label={t("planningBasis")}
              value={planStrategy}
              size="small"
              onChange={event => {
                setPlanStrategy(event.target.value);
              }}>
              <MenuItem value="forward" key="forward"> {t("planningBasisEarliestAvailable")}</MenuItem>
              <MenuItem value="forward2" key="forward2"> {t("planningBasisDueDatePullIn")}</MenuItem>
            </Select>
          </FormControl>
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
          <InputLabel>{t("backorder")}</InputLabel>
          <Select
            variant="outlined"
            name="backorder"
            label={t("backorder")}
            value={backorder}
            size="small"
            onChange={event => {
              setBackorder(event.target.value);
            }}>
            <MenuItem value="notAllow" key="notAllow"> {t("notAllow")}</MenuItem>
            <MenuItem value="allow" key="allow"> {t("allow")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {/* Empty Grid item for 3-column alignment - hidden on small screens */}
      <Grid item md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />

      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
          <InputLabel>{t(SAFETY_STOCK_POLICY.name)}</InputLabel>
          <Select
            variant="outlined"
            name={SAFETY_STOCK_POLICY.name}
            label={t(SAFETY_STOCK_POLICY.name)}
            value={safetyStockPolicy}
            size="small"
            onChange={event => {
              setSafetyStockPolicy(event.target.value);
            }}>
            <MenuItem value="NONE" key="NONE">{t("notUse")}</MenuItem>
            <MenuItem value="TargetStock" key="TargetStock">{t("targetStock")}</MenuItem>
            <MenuItem value="MinMax" key="MinMax">{t("minMax")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }} disabled={kitPlanMode && userRoles.includes(ROLE_APP_RP)}>
          <InputLabel>{t(ALLOW_SEMI_ORDER.name)}</InputLabel>
          <Select
            variant="outlined"
            name={ALLOW_SEMI_ORDER.name}
            label={t(ALLOW_SEMI_ORDER.name)}
            value={allowSemiOrder}
            size="small"
            onChange={event => {
              setAllowSemiOrder(event.target.value);
            }}>
            <MenuItem value={false} key="notAllow"> {t("notAllow")}</MenuItem>
            <MenuItem value={true} key="allow"> {t("allow")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {/* Empty Grid item for 3-column alignment - hidden on small screens */}
      <Grid item md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />

      <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
        <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
          <InputLabel>{t(ALT_RES_POLICY.name)}</InputLabel>
          <Select
            variant="outlined"
            name={ALT_RES_POLICY.name}
            label={t(ALT_RES_POLICY.name)}
            value={alterResPolicy}
            size="small"
            onChange={event => {
              setAlterResPolicy(event.target.value);
            }}>
            <MenuItem value={FAST} key={FAST}> {t(FAST)}</MenuItem>
            <MenuItem value={PRIORITY} key={PRIORITY}> {t(PRIORITY)}</MenuItem>
            <MenuItem value={MASTER_DATA} key={MASTER_DATA}> {t(MASTER_DATA)}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {userRoles.includes(ROLE_APP_RP) ? (
        <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1 }}>
            <FormControl variant="outlined" sx={{ width: "100%", minWidth: "200px" }}>
            <InputLabel>{t(KIT_PLAN_MODE.name)}</InputLabel>
            <Select
              variant="outlined"
              name={KIT_PLAN_MODE.name}
              label={t(KIT_PLAN_MODE.name)}
              value={kitPlanMode}
              size="small"
              onChange={event => {
                const value = event.target.value;
                if (value) {
                  setAllowSemiOrder(false);
                }
                setKitPlanMode(value);
              }}>
              <MenuItem value={false} key="notAllow"> {t(itemType.FG.name)}</MenuItem>
              <MenuItem value={true} key="allow"> {t(itemType.KIT.name)}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      ) : null}
    </Grid>
  );
};

const ModuleVersionSetting = ({ t, moduleCd, config, setConfig, userRoles = [] }) => {
  const getSalesPeriodRef = obj => {
    if (Object.prototype.hasOwnProperty.call(obj, SALES_REF_START) && Object.prototype.hasOwnProperty.call(obj, SALES_REF_END)) {
      const salesRefStart = Object.prototype.hasOwnProperty.call(obj, SALES_REF_START) ? obj.SALES_REF_START : configDefaultValue.salesRefStart;
      const salesRefEnd = Object.prototype.hasOwnProperty.call(obj, SALES_REF_END) ? obj.SALES_REF_END : configDefaultValue.salesRefEnd;
      return [salesRefStart, salesRefEnd];
    } else {
      return [configDefaultValue.salesRefStart, configDefaultValue.salesRefEnd];
    }
  }

  const getDemandPeriodRef = obj => {
    if (Object.prototype.hasOwnProperty.call(obj, DEMAND_REF_START) && Object.prototype.hasOwnProperty.call(obj, DEMAND_REF_END)) {
      const demandRefStart = Object.prototype.hasOwnProperty.call(obj, DEMAND_REF_START) ? obj.DEMAND_REF_START : configDefaultValue.demandRefStartDate;
      const demandRefEnd = Object.prototype.hasOwnProperty.call(obj, DEMAND_REF_END) ? obj.DEMAND_REF_END : configDefaultValue.demandRefEndDate;
      return [demandRefStart, demandRefEnd];
    } else {
      return [configDefaultValue.demandRefStartDate, configDefaultValue.demandRefEndDate];
    }
  }

  const getInitPlanRange = (cfg) => {
    if (cfg?.PLANNING_RANGE) return cfg.PLANNING_RANGE;
    if (moduleCd === module.MP && Array.isArray(cfg?.TIME_BUCKETS) && cfg.TIME_BUCKETS.length > 0) {
      return cfg.TIME_BUCKETS.reduce((sum, tb) => sum + (tb.COUNT || 0), 0);
    }
    return configDefaultValue.range;
  };

  const getInitBucketUnit = (cfg) => {
    if (cfg?.BUCKET_UNIT) return cfg.BUCKET_UNIT;
    return configDefaultValue.bucket;
  };

  const getInitTimeBuckets = (cfg) => {
    let tbs;
    if (moduleCd === module.MP && Array.isArray(cfg?.TIME_BUCKETS) && cfg.TIME_BUCKETS.length > 0) {
      tbs = cfg.TIME_BUCKETS;
    } else {
      tbs = [{ UNIT: getInitBucketUnit(cfg), COUNT: getInitPlanRange(cfg) }];
    }
    const planStart = cfg?.PLAN_START_DATE ? new ZDate(cfg.PLAN_START_DATE) : null;
    return initializeTimeBuckets(planStart, tbs).newTbs;
  };

  const [planRange, setPlanRange] = useState(() => getInitPlanRange(config));
  const [bucketUnit, setBucketUnit] = useState(() => getInitBucketUnit(config));
  const [timeBuckets, setTimeBuckets] = useState(() => getInitTimeBuckets(config));
  const [demandDistributionPolicy, setDemandDistributionPolicy] = useState(config?.DEMAND_DISTRIBUTION_POLICY ?? configDefaultValue.demandDistributionPolicy);
  const [salesPeriodRef, setSalesPeriodRef] = useState(config ? getSalesPeriodRef(config) : [configDefaultValue.salesRefStart, configDefaultValue.salesRefEnd]);
  const [demandPeriodRef, setDemandPeriodRef] = useState(config ? getDemandPeriodRef(config) : [configDefaultValue.demandRefStartDate, configDefaultValue.demandRefEndDate]);
  const [kitDemandMergeFlg, setKitDemandMergeFlg] = useState(config?.KIT_DEMAND_MERGE_FLG ?? false);
  const [noteFlg, setNoteFlg] = useState(config?.NOTE_FLG ?? false);
  const [multiSourcingFlg, setMultiSourcingFlg] = useState(config?.MULTI_SOURCING_FLG ?? false);
  const [useTargetSupplyDays, setUseTargetSupplyDays] = useState(config?.USE_TARGET_SUPPLY_DAYS ?? false);
  const [backorder, setBackorder] = useState(config?.BACKORDER ?? configDefaultValue.backorder);
  const [frozenRange, setFrozenRange] = useState(config?.FROZEN_RANGE ?? mpConfigDefaultValue.frozenRange);
  const [immediateShipment, setImmediateShipment] = useState(config?.IMMEDIATE_SHIPMENT ?? configDefaultValue.immediateShipment);
  const [resourceCapacity, setResourceCapacity] = useState(config?.RC ?? mpConfigDefaultValue.resourceCapacity);
  const [materialConstraint, setMaterialConstraint] = useState(config?.MC ?? mpConfigDefaultValue.materialConst);
  const [planStrategy, setPlanStrategy] = useState(config?.PS ?? mpConfigDefaultValue.planStrategy);
  const [safetyStockPolicy, setSafetyStockPolicy] = useState(config?.SAFETY_STOCK_POLICY ?? mpConfigDefaultValue.safetyStockPolicy);
  const [kitPlanMode, setKitPlanMode] = useState(config?.KIT_PLAN_MODE ?? mpConfigDefaultValue.kitPlanMode);
  const [allowSemiOrder, setAllowSemiOrder] = useState(config?.ALLOW_SEMI_ORDER ?? mpConfigDefaultValue.allowSemiOrder);
  const [alterResPolicy, setAlterResPolicy] = useState(config?.ALT_RES_POLICY ?? mpConfigDefaultValue.altResPolicy);
  const [enableRollingPlan, setEnableRollingPlan] = useState(config?.ENABLE_ROLLING_PLAN ?? mpConfigDefaultValue.enableRollingPlan);

  const mpFirstBucketSize = Math.max(1, timeBuckets[0]?.COUNT ?? 1);

  const onChangeSliderValueHandler = (_, newValue, type) => {
    if (isEqual(type, SALES)) {
      setSalesPeriodRef([newValue[0], newValue[1]]);
    } else if (isEqual(type, DEMAND)) {
      setDemandPeriodRef([newValue[0], newValue[1]]);
    }
  };

  const handleCheckbox = (e, name) => {
    const value = e.target.checked;
    if (name === "noteFlg") {
      setNoteFlg(value);
    } else if (name === "kitSalesDemandMergeFlg") {
      setKitDemandMergeFlg(value);
    } else if (name === "useTargetSupplyDays") {
      setUseTargetSupplyDays(value);
    } else if (name === "multiSourcingFlg") {
      setMultiSourcingFlg(value);
    }
  }

  const handleChangeBucket = (value) => {
    if (value === WEEK || value === PARTIAL_WEEK) {
      setPlanRange(12);
    } else if (value === MONTH) {
      setPlanRange(3);
    } else if (value === DAY) {
      setPlanRange(90);
    }
    setBucketUnit(value);
  }

  const handleConfigMp = () => {
    const normalizedFrozenRange = Math.min(Math.max(Number(frozenRange) || 0, 0), mpFirstBucketSize);

    // eslint-disable-next-line no-unused-vars
    const { TIME_BUCKET_SEGMENTS, TIME_BUCKET_STRATEGY_MODE, BUCKET_UNIT: _bu, ...restConfig } = config ?? {};
    setConfig({
      ...restConfig,
      PLANNING_RANGE: timeBuckets.reduce((s, tb) => s + (tb.COUNT || 0), 0),
      TIME_BUCKETS: timeBuckets,
      BACKORDER: backorder,
      FROZEN_RANGE: normalizedFrozenRange,
      MC: materialConstraint,
      RC: resourceCapacity,
      PS: planStrategy,
      SAFETY_STOCK_POLICY: safetyStockPolicy,
      KIT_PLAN_MODE: userRoles.includes(ROLE_APP_RP) ? kitPlanMode : false,
      ALLOW_SEMI_ORDER: kitPlanMode ? false : allowSemiOrder,
      ALT_RES_POLICY: alterResPolicy,
      ENABLE_ROLLING_PLAN: enableRollingPlan
    })
  }

  const handleConfigRp = () => {
    setConfig({
      ...config,
      BUCKET_UNIT: bucketUnit,
      PLANNING_RANGE: planRange,
      DEMAND_DISTRIBUTION_POLICY: demandDistributionPolicy,
      SALES_REF_START: salesPeriodRef ? salesPeriodRef[0] : configDefaultValue.salesRefStart,
      SALES_REF_END: salesPeriodRef ? salesPeriodRef[1] : configDefaultValue.salesRefEnd,
      DEMAND_REF_START: demandPeriodRef ? demandPeriodRef[0] : configDefaultValue.demandRefStartDate,
      DEMAND_REF_END: demandPeriodRef ? demandPeriodRef[1] : configDefaultValue.demandRefEndDate,
      KIT_DEMAND_MERGE_FLG: kitDemandMergeFlg,
      NOTE_FLG: noteFlg,
      MULTI_SOURCING_FLG: multiSourcingFlg,
      USE_TARGET_SUPPLY_DAYS: useTargetSupplyDays,
      BACKORDER: backorder,
      FROZEN_RANGE: frozenRange,
      IMMEDIATE_SHIPMENT: immediateShipment
    })
  }

  useEffect(() => {
    if (moduleCd === module.MP) {
      handleConfigMp();
    } else {
      handleConfigRp();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit, timeBuckets, demandDistributionPolicy, salesPeriodRef, demandPeriodRef, kitDemandMergeFlg, noteFlg, multiSourcingFlg, useTargetSupplyDays,
    backorder, frozenRange, immediateShipment, materialConstraint, resourceCapacity, planStrategy,
    safetyStockPolicy, kitPlanMode, allowSemiOrder, moduleCd, alterResPolicy, planRange, enableRollingPlan]);

  useEffect(() => {
    if (moduleCd !== module.MP) {
      return;
    }

    if (frozenRange > mpFirstBucketSize) {
      setFrozenRange(mpFirstBucketSize);
    }
  }, [moduleCd, frozenRange, mpFirstBucketSize]);

  const handleBlur = () => {
    if (moduleCd === module.MP) {
      handleConfigMp();
    } else {
      handleConfigRp();
    }
  };

  return (
    <Box className="content-main">
      <Box sx={{
        width: "100%",
        maxWidth: "966px",
        "@media (max-width: 1100px)": {
          maxWidth: "100%"
        }
      }}>
        {/* Common Settings */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ display: "inline-block", fontWeight: 700 }}>
            {t("planningBucket")}
          </Typography>
        </Box>
        <Grid container spacing={2} sx={{ pl: 0, mb: 1 }}>
          {moduleCd === module.MP ? (
            <Grid item xs={12} sx={{ pl: 0 }}>
              <MpBucketStrategySettings
                t={t}
                timeBuckets={timeBuckets}
                setTimeBuckets={setTimeBuckets}
                planStartDate={config?.PLAN_START_DATE ? new ZDate(config.PLAN_START_DATE) : null}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sx={{ pl: 0 }}>
              <Grid container spacing={2} sx={{ pl: 0, mb: 1 }}>
                <Grid item xs={12} sm={6} md={4} sx={{ pl: 0 }}>
                  <FormControl sx={{ width: "100%", minWidth: "200px" }}>
                    <InputLabel>{t("planningBucket")}</InputLabel>
                    <Select
                      variant={"outlined"}
                      name="bucketUnit"
                      label={t("planningBucket")}
                      value={bucketUnit}
                      size="small"
                      onChange={event => {
                        handleChangeBucket(event.target.value);
                      }}>
                      {Object.values(RP_TIME_DIMENSIOMS).map(level => {
                        return (
                          <MenuItem
                            value={level.cd}
                            key={level.cd}>
                            {t(level.name)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />
                <Grid item md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />
              </Grid>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ display: "inline-block", fontWeight: 700 }}>
            {t("planHorizon")}
          </Typography>
        </Box>
        <Grid container spacing={2} sx={{ pl: 0, mb: 1 }}>
          <Grid item xs={12} sx={{ pl: 0 }}>
            <Box sx={{ width: "100%" }}>
              <Grid container spacing={2} sx={{ pl: 0 }}>
                <Grid item xs={12} sm={6} md={4} sx={{ pl: 0 }}>
            <FormControl size="small" sx={{ width: "100%", minWidth: "200px" }}>
              <TextField
                label={t("planRange")}
                size="small"
                value={moduleCd === module.MP ? timeBuckets.reduce((s, tb) => s + (tb.COUNT || 0), 0) : planRange}
                name="planRange"
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1, readOnly: moduleCd === module.MP }}
                sx={{ textAlign: "center" }}
                onChange={event => {
                  if (moduleCd === module.MP) return;
                  const newValue = parseInt(event.target.value, 10);
                  if (isNaN(newValue) || newValue <= 0) {
                    return;
                  }
                  setPlanRange(newValue);
                }}
                onBlur={handleBlur}
              />
            </FormControl>
          </Grid>

          {/* MP Module added settings to the Planning Bucket area. */}
          {moduleCd === module.MP ?
            <Grid item xs={12} sm={6} md={4} sx={{ pl: 0 }}>
              <FormControl size="small" sx={{ width: "100%", minWidth: "200px" }}>
                <TextField
                  label={t("frozenRange")}
                  size="small"
                  value={frozenRange}
                  name="frozenRange"
                  type="number"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0, max: mpFirstBucketSize }}
                  sx={{ textAlign: "center" }}
                  onChange={event => {
                    const newValue = parseInt(event.target.value, 10);
                    if (isNaN(newValue) || newValue < 0) {
                      return;
                    }
                    setFrozenRange(Math.min(newValue, mpFirstBucketSize));
                  }}
                  onBlur={handleBlur}
                />
              </FormControl>
            </Grid>
            : null
          }

          {/* MP Module: Rolling Plan toggle - end of the plan horizon area. */}
          {moduleCd === module.MP ?
            <Grid item xs={12} sx={{ pl: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <StyledSwitch
                  size="small"
                  checked={enableRollingPlan}
                  onChange={e => setEnableRollingPlan(e.target.checked)}
                />
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  {t("enableRollingPlan")}
                </Typography>
              </Box>
            </Grid>
            : null
          }

          {/* RP Module added settings to the Planning Bucket area. */}
          {moduleCd === module.RP ?
            <>
              <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />
              <Grid item xs={12} sm={6} md={4} sx={{ pl: 0, mb: 1, display: { xs: "none", sm: "none", md: "block" } }} />
              <Grid item xs={12} sm={8} md={8} sx={{ p: 0, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>{t("demandRefPeriodForAvgSales")}</Typography>
                <Box sx={{ width: "100%", minWidth: "200px" }}>
                  <RpPeriodSlider salesPeriodRef={salesPeriodRef} demandPeriodRef={demandPeriodRef} onChangeSliderValueHandler={onChangeSliderValueHandler} isOnlySlider />
                </Box>
              </Grid>

              {/* Demand Distribution Policy - Full width */}
              <Grid item xs={12} sx={{ pl: 0, mb: 1 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>{t("demandDistributionPolicy")}</Typography>
                <FormControl size="small" sx={{ width: "100%" }}>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={demandDistributionPolicy}
                    onChange={event => {
                      setDemandDistributionPolicy(event.target.value);
                    }}>
                    <FormControlLabel value={EQUAL_BY_BUCKET} control={<Radio />} label={t("equalByBucket")} />
                    <FormControlLabel value={LAST_OF_BUCKET} control={<Radio />} label={t("lastOfBucket")} />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>
            : null
          }
        </Grid>
        </Box>
        </Grid>
        </Grid>

        <Box sx={{ mb: 1, mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h6" sx={{ display: "inline-block", fontWeight: 700 }}>
            {t("planPolicy")}
          </Typography>
          <PlanStrategyHelp />
        </Box>
        <Grid container spacing={2} sx={{ pl: 0, mb: 1 }}>
        <Grid item xs={12} sx={{ pl: 0 }}>
        <Box sx={{ width: "100%" }}>
          {/* RP Module Specific Settings */}
          {moduleCd === module.RP ?
            <RpModuleSettings
              t={t}
              kitDemandMergeFlg={kitDemandMergeFlg}
              noteFlg={noteFlg}
              multiSourcingFlg={multiSourcingFlg}
              useTargetSupplyDays={useTargetSupplyDays}
              immediateShipment={immediateShipment}
              setImmediateShipment={setImmediateShipment}
              handleCheckbox={handleCheckbox}
              backorder={backorder}
              setBackorder={setBackorder}
            />
            : null
          }

          {/* MP Module Specific Settings */}
          {moduleCd === module.MP ?
            <MpModuleSettings
              t={t}
              userRoles={userRoles}
              resourceCapacity={resourceCapacity}
              setResourceCapacity={setResourceCapacity}
              materialConstraint={materialConstraint}
              setMaterialConstraint={setMaterialConstraint}
              planStrategy={planStrategy}
              setPlanStrategy={setPlanStrategy}
              backorder={backorder}
              setBackorder={setBackorder}
              safetyStockPolicy={safetyStockPolicy}
              setSafetyStockPolicy={setSafetyStockPolicy}
              kitPlanMode={kitPlanMode}
              setKitPlanMode={setKitPlanMode}
              allowSemiOrder={allowSemiOrder}
              setAllowSemiOrder={setAllowSemiOrder}
              alterResPolicy={alterResPolicy}
              setAlterResPolicy={setAlterResPolicy}
            />
            : null
          }
        </Box>
        </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export { MpBucketStrategySettings, buildDefaultBucketSegments, enforceBucketProgression };
export default withTranslation()(ModuleVersionSetting);
